/**
 * The Agent Control Center. ARCHITECTURE.md section 9.
 *
 * The Sixgen Brain at the centre, agents arranged around it, an edge drawn when one agent's run
 * caused another's.
 *
 * CLAUDE.md 3.3 governs every line of this file:
 *
 *  - No state is set here. `state` comes from `/api/control-center`, which reads
 *    `derived.agent_status` and nothing else. There is no local status, no optimistic update, and
 *    no animation that outlives the run it depends on.
 *  - Nothing pulses without a run. The one animation is bound to `currentRunId`, so an idle graph
 *    is a still graph.
 *  - An empty Control Center is honest. In Phase 2 there is one agent and it is usually idle.
 *    The page says so in words rather than filling the space.
 *
 * The stream tells the page that something changed; the page then re-reads. It never renders the
 * notification payload, because a payload can disagree with the view and the view is the truth.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api, type AgentSummary, type ControlCenter, type RunDetail } from '../api.js';
import { AUTONOMY_LABEL, elapsed, layout, styleFor } from './control-center-view.js';

export function ControlCenterPage(): ReactNode {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [streamState, setStreamState] = useState<'connecting' | 'live' | 'offline'>('connecting');

  const graph = useQuery({
    queryKey: ['control-center'],
    queryFn: () => api.get<ControlCenter>('/api/control-center'),
    // A slow poll as a floor, so a dropped stream degrades to stale-by-seconds rather than
    // silently frozen. The stream is what makes it feel live.
    refetchInterval: 15_000,
  });

  useEffect(() => {
    const source = new EventSource('/api/control-center/stream', { withCredentials: true });
    source.onopen = () => setStreamState('live');
    source.onerror = () => setStreamState('offline');
    source.addEventListener('sixgen_control_center', () => {
      // Re-read. The notification says something changed, not what it changed to.
      void queryClient.invalidateQueries({ queryKey: ['control-center'] });
      void queryClient.invalidateQueries({ queryKey: ['agent'] });
    });
    return () => source.close();
  }, [queryClient]);

  const agents = graph.data?.agents ?? [];
  const positions = useMemo(() => layout(Math.max(agents.length, 1), 150), [agents.length]);
  const busyCount = agents.filter((a) => styleFor(a.state).busy).length;
  const killSwitch = graph.data?.killSwitch;

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Agent Control Center</h1>
          <p className="subtitle">
            Every state on this page is computed from stored run records. Nothing here is simulated.
          </p>
        </div>
        <span className={`stream stream-${streamState}`}>
          {streamState === 'live'
            ? 'Live'
            : streamState === 'connecting'
              ? 'Connecting…'
              : 'Reconnecting…'}
        </span>
      </header>

      {killSwitch?.allAgentsDisabled && (
        <div className="alert alert-warn">
          All agents are disabled by the kill switch. Every agent below shows OFFLINE for that
          reason, whatever its own configuration says.
        </div>
      )}

      {graph.isLoading && <p className="muted">Loading…</p>}
      {graph.isError && <p className="error">Could not load the Control Center.</p>}

      {graph.data && agents.length === 0 && (
        <div className="card empty">
          <h2>No agents are configured yet.</h2>
          <p>The first agent arrives with the CEO briefing.</p>
        </div>
      )}

      {agents.length > 0 && (
        <>
          <div className="control-center">
            <svg viewBox="-260 -220 520 440" role="img" aria-label="Agent activity graph">
              {/* Edges first, so nodes draw over them. */}
              {agents.map((agent, i) => {
                const p = positions[i]!;
                const busy = styleFor(agent.state).busy;
                return (
                  <line
                    key={`edge-${agent.id}`}
                    x1={0}
                    y1={0}
                    x2={p.x}
                    y2={p.y}
                    className={busy ? 'cc-edge active' : 'cc-edge'}
                    stroke={busy ? styleFor(agent.state).tone : '#1f2937'}
                  />
                );
              })}

              <circle r={54} className="cc-brain" />
              <text y={-4} className="cc-brain-label">
                SIXGEN
              </text>
              <text y={12} className="cc-brain-sub">
                BRAIN
              </text>

              {agents.map((agent, i) => {
                const p = positions[i]!;
                const style = styleFor(agent.state);
                return (
                  <g
                    key={agent.id}
                    transform={`translate(${p.x} ${p.y})`}
                    className={`cc-node${selected === agent.key ? ' selected' : ''}`}
                    onClick={() => setSelected(agent.key)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') setSelected(agent.key);
                    }}
                  >
                    {/* The only animation on this page, and it exists only while a run does. */}
                    {agent.currentRunId && style.busy && (
                      <circle r={34} className="cc-pulse" stroke={style.tone} />
                    )}
                    <circle r={30} fill={style.tone} className="cc-node-circle" />
                    {agent.pendingApprovals > 0 && (
                      <>
                        <circle cx={22} cy={-22} r={10} className="cc-badge" />
                        <text x={22} y={-18} className="cc-badge-text">
                          {agent.pendingApprovals}
                        </text>
                      </>
                    )}
                    <text y={48} className="cc-node-name">
                      {agent.name}
                    </text>
                    <text y={64} className="cc-node-state">
                      {style.label}
                    </text>
                  </g>
                );
              })}
            </svg>

            <p className="cc-summary">
              {busyCount === 0
                ? `${agents.length} agent${agents.length === 1 ? '' : 's'}, none running right now.`
                : `${busyCount} of ${agents.length} running.`}
            </p>
          </div>

          <table className="table">
            <thead>
              <tr>
                <th>Agent</th>
                <th>State</th>
                <th>Autonomy</th>
                <th>Current task</th>
                <th>Running for</th>
                <th>Waiting on you</th>
                <th>Run</th>
                <th>Kill switch</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((agent) => (
                <tr
                  key={agent.id}
                  onClick={() => setSelected(agent.key)}
                  className="clickable"
                  title="Open the detail drawer"
                >
                  <td>{agent.name}</td>
                  <td>
                    <span
                      className="state-dot"
                      style={{ background: styleFor(agent.state).tone }}
                    />
                    {styleFor(agent.state).label}
                  </td>
                  <td className="muted">
                    {AUTONOMY_LABEL[agent.autonomyLevel] ?? agent.autonomyLevel}
                  </td>
                  <td className="muted">{agent.currentTask ?? '—'}</td>
                  <td className="muted">{elapsed(agent.currentRunStartedAt)}</td>
                  <td>{agent.pendingApprovals > 0 ? agent.pendingApprovals : '—'}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <RunNowButton agentKey={agent.key} />
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <AgentStopButton
                      agentId={agent.id}
                      disabled={killSwitch?.disabledAgentIds.includes(agent.id) ?? false}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {selected && <AgentDrawer agentKey={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------

interface AgentDetail {
  agent: {
    id: string;
    key: string;
    name: string;
    purpose: string;
    autonomy_level: number;
    enabled: boolean;
    state: string;
    eval_suite_key: string | null;
    schedules: string[];
  };
  permissions: { kind: string; key: string; risk_override: string | null }[];
  runs: {
    id: string;
    trigger: string;
    task: string;
    status: string;
    confidence: string | null;
    started_at: string;
    ended_at: string | null;
    estimated_cost_cents: string | null;
  }[];
  evaluations: {
    id: string;
    suite: string;
    started_at: string;
    cases_total: number;
    cases_passed: number;
    met_threshold: boolean | null;
    threshold_detail: string | null;
  }[];
}

function AgentDrawer({ agentKey, onClose }: { agentKey: string; onClose: () => void }): ReactNode {
  const [openRun, setOpenRun] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: ['agent', agentKey],
    queryFn: () => api.get<AgentDetail>(`/api/agents/${agentKey}`),
  });

  const scopes = detail.data?.permissions.filter((p) => p.kind === 'data_scope') ?? [];
  const tools = detail.data?.permissions.filter((p) => p.kind === 'tool') ?? [];

  return (
    <div className="drawer-backdrop" onClick={onClose} role="presentation">
      <aside className="drawer" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal>
        <header className="drawer-header">
          <h2>{detail.data?.agent.name ?? agentKey}</h2>
          <button type="button" className="link" onClick={onClose}>
            Close
          </button>
        </header>

        {detail.isLoading && <p className="muted">Loading…</p>}
        {detail.isError && <p className="error">Could not load this agent.</p>}

        {detail.data && (
          <div className="drawer-body">
            <p>{detail.data.agent.purpose}</p>

            <dl className="kv">
              <dt>State</dt>
              <dd>{styleFor(detail.data.agent.state).label}</dd>
              <dt>Autonomy</dt>
              <dd>{AUTONOMY_LABEL[detail.data.agent.autonomy_level]}</dd>
              <dt>Enabled</dt>
              <dd>
                <AgentEnabledToggle
                  agentKey={detail.data.agent.key}
                  enabled={detail.data.agent.enabled}
                />
              </dd>
              <dt>Schedule</dt>
              <dd>{detail.data.agent.schedules.join(', ') || 'Not scheduled'}</dd>
            </dl>

            <h3>What it may read</h3>
            {scopes.length === 0 ? (
              <p className="muted">No data scopes. It cannot retrieve anything.</p>
            ) : (
              <ul className="chips">
                {scopes.map((s) => (
                  <li key={s.key} className="chip">
                    {s.key}
                  </li>
                ))}
              </ul>
            )}

            <h3>What it may do</h3>
            {tools.length === 0 ? (
              <p className="muted">
                No tools. This agent produces recommendations; it cannot act on anything.
              </p>
            ) : (
              <ul className="chips">
                {tools.map((t) => (
                  <li key={t.key} className="chip">
                    {t.key}
                    {t.risk_override && <span className="chip-tag">{t.risk_override}</span>}
                  </li>
                ))}
              </ul>
            )}

            <h3>Evaluations</h3>
            {detail.data.evaluations.length === 0 ? (
              <p className="muted">
                Never evaluated. Run <code>pnpm eval</code> to score it.
              </p>
            ) : (
              <table className="table compact">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Cases</th>
                    <th>Threshold</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.data.evaluations.map((e) => (
                    <tr key={e.id}>
                      <td>{new Date(e.started_at).toLocaleString()}</td>
                      <td>
                        {e.cases_passed}/{e.cases_total}
                      </td>
                      <td className={e.met_threshold ? 'ok' : 'error'}>
                        {e.met_threshold ? 'Met' : (e.threshold_detail ?? 'Not met')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <h3>Recent runs</h3>
            {detail.data.runs.length === 0 ? (
              <p className="muted">It has never run. That is not a fault; it is a fact.</p>
            ) : (
              <table className="table compact">
                <thead>
                  <tr>
                    <th>Started</th>
                    <th>Trigger</th>
                    <th>Task</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.data.runs.map((run) => (
                    <tr key={run.id} className="clickable" onClick={() => setOpenRun(run.id)}>
                      <td>{new Date(run.started_at).toLocaleString()}</td>
                      <td className="muted">{run.trigger}</td>
                      <td>{run.task}</td>
                      <td>{styleFor(run.status).label}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {openRun && <RunDetailPanel runId={openRun} onClose={() => setOpenRun(null)} />}
          </div>
        )}
      </aside>
    </div>
  );
}

/**
 * One run, in full. This is what "where did this come from?" is answered with: what it retrieved,
 * every state it passed through, and every tool it asked for — including the ones it was refused.
 */
function RunDetailPanel({ runId, onClose }: { runId: string; onClose: () => void }): ReactNode {
  const run = useQuery({
    queryKey: ['run', runId],
    queryFn: () => api.get<RunDetail>(`/api/runs/${runId}`),
  });

  if (run.isLoading) return <p className="muted">Loading run…</p>;
  if (!run.data) return <p className="error">Could not load this run.</p>;

  const manifest = run.data.timeline.find((t) => t.context_manifest)?.context_manifest ?? [];

  return (
    <div className="run-detail">
      <header className="drawer-header">
        <h3>Run detail</h3>
        <button type="button" className="link" onClick={onClose}>
          Close
        </button>
      </header>

      <h4>Data retrieved</h4>
      {manifest.length === 0 ? (
        <p className="muted">Nothing recorded. The run did not reach the retrieval step.</p>
      ) : (
        <ul className="chips">
          {manifest.map((m) => (
            <li key={m.scope} className="chip">
              {m.scope}
              <span className="chip-tag">
                {m.rowCount} row{m.rowCount === 1 ? '' : 's'}
                {m.truncated ? ' (truncated)' : ''}
              </span>
            </li>
          ))}
        </ul>
      )}

      <h4>What happened</h4>
      <ol className="timeline">
        {run.data.timeline.map((t) => (
          <li key={t.id}>
            <span className="timeline-time">{new Date(t.occurred_at).toLocaleTimeString()}</span>
            <span>{styleFor(t.to_status).label}</span>
            {t.detail && <span className="muted"> — {t.detail}</span>}
          </li>
        ))}
      </ol>

      <h4>Tool calls</h4>
      {run.data.toolCalls.length === 0 ? (
        <p className="muted">None. This agent has no tools.</p>
      ) : (
        <table className="table compact">
          <thead>
            <tr>
              <th>Tool</th>
              <th>Risk</th>
              <th>Decision</th>
              <th>Why</th>
            </tr>
          </thead>
          <tbody>
            {run.data.toolCalls.map((call) => (
              <tr key={call.id}>
                <td>{call.tool_key}</td>
                <td>{call.risk_class}</td>
                <td className={call.permission_decision === 'refuse' ? 'error' : ''}>
                  {call.permission_decision}
                </td>
                <td className="muted">{call.permission_reason ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {run.data.run.errors && (
        <>
          <h4>Error</h4>
          <p className="error">{run.data.run.errors.message}</p>
        </>
      )}

      {run.data.run.output !== null && run.data.run.output !== undefined && (
        <>
          <h4>Output</h4>
          <pre className="json">{JSON.stringify(run.data.run.output, null, 2)}</pre>
        </>
      )}
    </div>
  );
}

export type { AgentSummary };

/**
 * Per-agent kill switch. CLAUDE.md 11 lists six scopes; this is "disable one agent", and it lives
 * here rather than in Settings because this is the screen an operator is looking at when they
 * decide one agent has to stop.
 *
 * Stopping is never blocked, including while the platform is READ_ONLY — the API deliberately
 * omits its write guard on this path, so a stopped system is not a one-way door.
 */
function AgentStopButton({ agentId, disabled }: { agentId: string; disabled: boolean }): ReactNode {
  const queryClient = useQueryClient();

  const toggle = useMutation({
    mutationFn: (next: boolean) =>
      api.post('/api/kill-switch/target', {
        kind: 'agent',
        target: agentId,
        disabled: next,
        reason: next ? 'Disabled from the Control Center' : 'Re-enabled from the Control Center',
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['control-center'] });
      void queryClient.invalidateQueries({ queryKey: ['kill-switch'] });
    },
  });

  return (
    <button
      type="button"
      className={disabled ? 'secondary' : 'danger'}
      disabled={toggle.isPending}
      onClick={() => toggle.mutate(!disabled)}
      title={
        disabled
          ? 'This agent is stopped by the kill switch. Allow it to run again.'
          : 'Stop this agent. A run already in flight ends at its next check.'
      }
    >
      {toggle.isPending ? '…' : disabled ? 'Allow' : 'Stop'}
    </button>
  );
}

/**
 * Run an agent now instead of waiting for its schedule.
 *
 * The request only ENQUEUES — the worker does the run, on the same path the schedule uses. So this
 * reports "queued", never "done", and the graph shows the run when it actually starts. Claiming a
 * run finished because a POST returned 202 would be exactly the kind of invented activity
 * CLAUDE.md 3.3 forbids.
 */
function RunNowButton({ agentKey }: { agentKey: string }): ReactNode {
  const queryClient = useQueryClient();
  const [queued, setQueued] = useState(false);

  const run = useMutation({
    mutationFn: () => api.post(`/api/agents/${agentKey}/run`),
    onSuccess: () => {
      setQueued(true);
      // Nothing to show yet. The stream wakes the page when the run records its first transition.
      void queryClient.invalidateQueries({ queryKey: ['control-center'] });
    },
  });

  const message =
    run.error instanceof ApiError
      ? run.error.killSwitch
        ? 'The platform is READ-ONLY, so nothing was queued.'
        : run.error.message
      : null;

  return (
    <>
      <button
        type="button"
        className="secondary"
        disabled={run.isPending}
        onClick={() => run.mutate()}
        title="Queue a run now. The worker picks it up within a second."
      >
        {run.isPending ? 'Queueing…' : queued ? 'Queued' : 'Run now'}
      </button>
      {message && <div className="error small">{message}</div>}
    </>
  );
}

/**
 * Turn an agent on or off. This is the agent's own definition, not the kill switch.
 *
 * Three independent switches have to agree before a run happens: this one, the `agent.<key>`
 * feature flag in Settings, and the kill switch. Naming which one is off is the whole point —
 * an operator who cannot tell them apart toggles the wrong thing.
 */
function AgentEnabledToggle({
  agentKey,
  enabled,
}: {
  agentKey: string;
  enabled: boolean;
}): ReactNode {
  const queryClient = useQueryClient();

  const toggle = useMutation({
    mutationFn: (next: boolean) => api.post(`/api/agents/${agentKey}/enabled`, { enabled: next }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['agent'] });
      void queryClient.invalidateQueries({ queryKey: ['control-center'] });
    },
  });

  const message =
    toggle.error instanceof ApiError
      ? toggle.error.killSwitch
        ? 'The platform is READ-ONLY. Turning an agent on is blocked; turning one off is not.'
        : toggle.error.message
      : null;

  return (
    <>
      <span>{enabled ? 'Yes' : 'No — it will not run'}</span>{' '}
      <button
        type="button"
        className={enabled ? 'secondary' : 'primary'}
        disabled={toggle.isPending}
        onClick={() => toggle.mutate(!enabled)}
      >
        {toggle.isPending ? '…' : enabled ? 'Disable' : 'Enable'}
      </button>
      {message && <div className="error small">{message}</div>}
    </>
  );
}
